import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { updateUserPassword, signOut } from './authSlice';
import { auth, db } from '../../config/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LogoutIcon from '@mui/icons-material/Logout';
import GoogleIcon from '@mui/icons-material/Google';

const UserSettings = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const [loading, setLoading] = useState(true);
  const [isGoogleUser, setIsGoogleUser] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }

    const fetchUserSettings = async () => {
      try {
        // 現在のユーザーの認証プロバイダーを確認
        const currentUser = auth.currentUser;
        if (currentUser) {
          const providerData = currentUser.providerData;
          const hasGoogleProvider = providerData.some(
            provider => provider.providerId === 'google.com'
          );
          setIsGoogleUser(hasGoogleProvider);
        }

        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (!userDoc.exists()) {
          // 初回アクセス時にユーザードキュメントを作成
          await setDoc(userDocRef, {
            displayName: user.displayName || '',
            email: user.email,
            createdAt: new Date()
          });
        }
      } catch (error) {
        console.error('ユーザー設定の取得に失敗:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserSettings();
  }, [user, navigate]);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });

  const validatePassword = (password) => {
    if (password.length < 8) {
      return 'パスワードは8文字以上である必要があります';
    }
    if (!/\d/.test(password)) {
      return 'パスワードには数字を含める必要があります';
    }
    return '';
  };

  const handleLogout = async () => {
    try {
      await dispatch(signOut());
      navigate('/');
    } catch (error) {
      console.error('ログアウトに失敗:', error);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordMessage({ type: 'error', text: '全ての項目を入力してください' });
      return;
    }

    const passwordValidationError = validatePassword(newPassword);
    if (passwordValidationError) {
      setPasswordMessage({ type: 'error', text: passwordValidationError });
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordMessage({ type: 'error', text: '新しいパスワードが一致しません' });
      return;
    }

    try {
      await dispatch(updateUserPassword({ currentPassword, newPassword })).unwrap();
      setPasswordMessage({ type: 'success', text: 'パスワードを更新しました' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      setPasswordMessage({ type: 'error', text: error.message });
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <IconButton onClick={() => navigate('/')} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Button
          variant="outlined"
          color="error"
          onClick={handleLogout}
          startIcon={<LogoutIcon />}
        >
          ログアウト
        </Button>
      </Box>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" component="h1" gutterBottom>
          ユーザー設定
        </Typography>

        {isGoogleUser && (
          <Box sx={{ mb: 3 }}>
            <Chip
              icon={<GoogleIcon />}
              label="Googleアカウントでログイン中"
              color="primary"
              variant="outlined"
            />
          </Box>
        )}

        {!isGoogleUser && (
          <>
            <Box component="form" onSubmit={handlePasswordUpdate} sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                パスワードの変更
              </Typography>
              {passwordMessage.text && (
                <Alert severity={passwordMessage.type} sx={{ mb: 2 }}>
                  {passwordMessage.text}
                </Alert>
              )}
              <TextField
                fullWidth
                type="password"
                label="現在のパスワード"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                margin="normal"
              />
              <TextField
                fullWidth
                type="password"
                label="新しいパスワード"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                margin="normal"
                helperText="8文字以上で、数字を1文字以上含める必要があります"
              />
              <TextField
                fullWidth
                type="password"
                label="新しいパスワード（確認）"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                margin="normal"
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                sx={{ mt: 2 }}
              >
                パスワードを更新
              </Button>
            </Box>
          </>
        )}

        {isGoogleUser && (
          <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Googleアカウントでログインしているため、パスワードの変更はGoogleアカウントの設定から行ってください。
            </Typography>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default UserSettings;
